import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

/**
 * One place that decides how a notification LOOKS.
 *
 * The first release rendered every row as title + "Category · time", which
 * made a feed of 30 buy-alert matches indistinguishable. Both surfaces (bell
 * popover and /notifications) now share this module so a change to grouping,
 * icons or copy lands in both.
 */

export const CATEGORY_LABELS = {
  pulse: "Pulse",
  alert: "Alert",
  notification: "Notification",
};

export const CATEGORY_TONES = {
  pulse: "purple",
  alert: "danger",
  notification: "muted",
};

/**
 * Event → group. Grouping is by KIND of event, not by category: the point is
 * that 30 buy-alert matches collapse into one titled section instead of
 * burying the single payment that actually needs attention.
 *
 * `order` fixes section order on screen — money and leads first, background
 * noise last — while rows inside a section stay newest-first.
 */
export const EVENT_GROUPS = {
  "lead.created": { key: "leads", label: "Leads", order: 1 },
  "order_request.created": { key: "leads", label: "Leads", order: 1 },
  "order.placed": { key: "orders", label: "Client orders", order: 2 },
  "payment.deposit_paid": { key: "payments", label: "Payments", order: 3 },
  "payment.received": { key: "payments", label: "Payments", order: 3 },
  "payment.bank_transfer_pending": { key: "payments", label: "Payments", order: 3 },
  "quote.accepted": { key: "quotes", label: "Quotes", order: 4 },
  "quote.expiring": { key: "quotes", label: "Quotes", order: 4 },
  "repair.return_due_soon": { key: "repairs", label: "Repairs", order: 5 },
  "repair.return_due": { key: "repairs", label: "Repairs", order: 5 },
  "buy_alert.match": { key: "buying", label: "Buying opportunities", order: 6 },
  "buy_opportunity.detected": { key: "buying", label: "Buying opportunities", order: 6 },
  "order.dispatched": { key: "shipping", label: "Shipping", order: 7 },
  "order.delivered": { key: "shipping", label: "Shipping", order: 7 },
  "user.signed_up": { key: "people", label: "People", order: 8 },
  "user.kyc_failed": { key: "people", label: "People", order: 8 },
  "product.requested": { key: "catalogue", label: "Catalogue", order: 9 },
  "wa_broadcast.paused": { key: "broadcast", label: "WhatsApp Broadcast", order: 10 },
  "wa_broadcast.opt_out": { key: "broadcast", label: "WhatsApp Broadcast", order: 10 },
};

const FALLBACK_GROUP = { key: "other", label: "Other", order: 99 };

export const groupFor = (eventType) => EVENT_GROUPS[eventType] || FALLBACK_GROUP;

/** The watch line: "Rolex Submariner · 126610LN", from the data payload. */
export const watchLabel = (data = {}) => {
  const name = [data.brand, data.model].filter(Boolean).join(" ");
  if (name && data.reference) return `${name} · ${data.reference}`;
  return name || data.reference || "";
};

export const timeAgo = (iso) => (iso ? dayjs(iso).fromNow() : "");

/**
 * Sort into ordered sections: sections by their fixed `order`, rows inside a
 * section newest-first (the API already returns newest-first; grouping is
 * stable so that order is preserved).
 */
export const groupNotifications = (items = []) => {
  const buckets = new Map();

  items.forEach((item) => {
    const group = groupFor(item.eventType);
    if (!buckets.has(group.key)) {
      buckets.set(group.key, { ...group, items: [] });
    }
    buckets.get(group.key).items.push(item);
  });

  return [...buckets.values()]
    .map((bucket) => ({
      ...bucket,
      items: [...bucket.items].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      ),
      unread: bucket.items.filter((i) => !i.isRead).length,
    }))
    .sort((a, b) => a.order - b.order);
};
